import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useConsultantVisibleContext } from "../../features/consultant/screenContextAdapter";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FolderKanban, Plus, Search, X } from "lucide-react";
import Swal from "sweetalert2";
import { Toast } from "../../helpers/alert";
import GuidedImpactCalculation from "../../features/calculationModels/GuidedImpactCalculation";
import { createCalculationModelsApi } from "../../features/calculationModels/api";
import { projectModelCode, versionIdOf } from "../../features/calculationModels/projectCalculationModel";
import "../../features/calculationModels/projectCalculationModel.css";
import {
  createProject,
  getProjectBranches,
  getProjectCompanies,
  getProjectDevices,
  getProjectUsers,
  getProjects,
} from "../../redux/apiCalls/projectsCall/projectsCall";
import { convertAgentRecommendationToProject } from "../../redux/apiCalls/recommendationsCall/recommendationsCall";
import {
  getUserOrgIds,
  isMaster,
  userMatchesSelectionByIds,
} from "../../helpers/healthsafetyreports.helpers";
import {
  canManageProjects,
  DEVICE_TYPES,
  deviceMatchesScope,
  EQUIPMENT_FUNCTIONS,
  formatCurrency,
  formatDate,
  formatProgress,
  formatStatusLabel,
  getBranchId,
  getBranchLabel,
  getCompanyId,
  getCompanyLabel,
  getDeviceLabel,
  getScopeFromProjectForm,
  getUserLabel,
  PARTICIPANT_RESPONSIBILITIES,
  PROJECT_STATUSES,
  TASK_STATUSES,
  TASK_TYPES,
  toNullableNumber,
  toSelectOptions,
} from "./projects.helpers";
import "./Projects.css";
import "../AgentRecommendations/AgentRecommendations.css";

const emptyProjectForm = {
  title: "",
  description: "",
  status: "propuesto",
  progress_percentage: 0,
  company_id: "",
  branch_id: "",
  start_date: "",
  planned_end_date: "",
  actual_end_date: "",
  planned_investment_total: "",
  actual_investment_total: "",
  planned_saving: "",
  actual_saving: "",
  participants: [],
  equipment: [],
  tasks: [],
};

const emptyParticipant = {
  user_id: "",
  role: "otro",
  responsibility: "",
  notes: "",
};

const emptyEquipment = {
  device_id: "",
  device_name: "",
  device_type: "",
  description: "",
  function_in_project: "",
};

const emptyTask = {
  title: "",
  description: "",
  status: "pendiente",
  task_type: "actividad",
  assigned_to: "",
  start_date: "",
  due_date: "",
  planned_hours: "",
  planned_cost: "",
  progress_percentage: 0,
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const dateInputValue = (value) => value ? String(value).slice(0, 10) : "";

const normalizeSuggestedTasks = (tasks) =>
  (Array.isArray(tasks) ? tasks : []).map((task, index) => {
    const item = typeof task === "string" ? { title: task } : task || {};
    return {
      ...emptyTask,
      title: firstDefined(item.title, item.name, item.task, `Tarea ${index + 1}`),
      description: firstDefined(item.description, item.details, ""),
      assigned_to: firstDefined(item.assigned_to, item.user_id, ""),
      start_date: dateInputValue(item.start_date),
      due_date: dateInputValue(firstDefined(item.due_date, item.suggested_due_date)),
      planned_hours: firstDefined(item.planned_hours, item.estimated_hours, ""),
      planned_cost: firstDefined(item.planned_cost, item.estimated_cost, ""),
    };
  });

const buildSuggestedDescription = (suggestion, recommendation) => {
  const problem = firstDefined(
    suggestion.problem_description,
    suggestion.description,
    recommendation?.problem_detected,
    "",
  );
  const solution = firstDefined(suggestion.proposed_solution, "");

  if (!solution || solution === problem) return problem;
  return [problem, `Solución propuesta:\n${solution}`].filter(Boolean).join("\n\n");
};

const buildRecommendationProjectForm = ({ recommendation, projectSuggestion }) => {
  const suggestion = projectSuggestion && typeof projectSuggestion === "object"
    ? projectSuggestion
    : {};
  const ownerId = firstDefined(
    suggestion.suggested_owner_user_id,
    suggestion.responsible_user_id,
    recommendation?.suggested_owner_user_id,
  );
  const equipment = recommendation?.id_device || recommendation?.id_equipos
    ? [{
        ...emptyEquipment,
        device_id: recommendation.id_device ? String(recommendation.id_device) : "",
        device_name:
          recommendation.device?.name || recommendation.equipo?.nombre || "",
        device_type: recommendation.equipo?.tipo || "",
        description: recommendation.equipo?.nombre || "",
        function_in_project: "medicion",
      }]
    : [];

  return {
    ...emptyProjectForm,
    title: firstDefined(
      suggestion.project_name,
      suggestion.name,
      suggestion.title,
      recommendation?.title,
      "",
    ),
    description: buildSuggestedDescription(suggestion, recommendation),
    company_id: recommendation?.id_company ? String(recommendation.id_company) : "",
    branch_id: recommendation?.id_branch_offices
      ? String(recommendation.id_branch_offices)
      : "",
    start_date: dateInputValue(suggestion.suggested_start_date),
    planned_end_date: dateInputValue(firstDefined(
      suggestion.suggested_due_date,
      suggestion.planned_end_date,
      recommendation?.suggested_due_date,
    )),
    planned_investment_total: firstDefined(
      suggestion.estimated_project_cost_value,
      suggestion.planned_investment_total,
      "",
    ),
    planned_saving: firstDefined(
      suggestion.estimated_economic_saving_value,
      suggestion.planned_saving,
      "",
    ),
    participants: ownerId
      ? [{
          ...emptyParticipant,
          user_id: String(ownerId),
          responsibility: "Responsable del proyecto",
        }]
      : [],
    equipment,
    tasks: normalizeSuggestedTasks(suggestion.initial_tasks),
  };
};

const getDeviceId = (device) => device?.id_device ?? device?.id;
const getDeviceType = (device) =>
  device?.device_type ||
  device?.type ||
  device?.equipment_type ||
  device?.primary_group ||
  "";

function Projects() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useSelector((state) => state.user);
  const token = currentUser?.accessToken;
  const canManage = canManageProjects(currentUser?.privilege);

  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState(emptyProjectForm);
  const [filters, setFilters] = useState({ status: "", text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [conversionSource, setConversionSource] = useState(null);
  const [configureCalculation, setConfigureCalculation] = useState(false);
  const [calculationDefinition, setCalculationDefinition] = useState(null);
  const calculationApi = useMemo(() => createCalculationModelsApi(token), [token]);
  useConsultantVisibleContext({ status: filters.status, active_tab: showCreateForm ? "Crear proyecto" : "Listado de proyectos" });

  const loadProjects = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getProjects(token);
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      Toast.fire({
        icon: "error",
        title: "No se pudieron cargar los proyectos",
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadOptions = useCallback(async () => {
    if (!token) return;
    setLoadingOptions(true);
    try {
      const [usersData, devicesData, branchesData, companiesData] =
        await Promise.allSettled([
          getProjectUsers(token),
          getProjectDevices(token),
          getProjectBranches(token),
          getProjectCompanies(token),
        ]);

      if (usersData.status === "fulfilled") {
        setUsers(Array.isArray(usersData.value) ? usersData.value : []);
      }
      if (devicesData.status === "fulfilled") {
        setDevices(Array.isArray(devicesData.value) ? devicesData.value : []);
      }
      if (branchesData.status === "fulfilled") {
        setBranches(Array.isArray(branchesData.value) ? branchesData.value : []);
      }
      if (companiesData.status === "fulfilled") {
        setCompanies(Array.isArray(companiesData.value) ? companiesData.value : []);
      }
    } finally {
      setLoadingOptions(false);
    }
  }, [token]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (showCreateForm) {
      loadOptions();
    }
  }, [loadOptions, showCreateForm]);

  useEffect(() => {
    const source = location.state?.recommendationConversion;
    if (!source || isMaster(currentUser)) return;

    setConversionSource(source);
    setFormData(buildRecommendationProjectForm(source));
    setShowCreateForm(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [currentUser, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!showCreateForm) return;
    const defaultCompanyId =
      companies.length === 1 ? getCompanyId(companies[0]) : currentUser?.id_company || "";
    if (defaultCompanyId) {
      setFormData((prev) => ({
        ...prev,
        company_id: prev.company_id || String(defaultCompanyId),
      }));
    }
  }, [companies, currentUser?.id_company, showCreateForm]);

  const selectedScope = useMemo(
    () => getScopeFromProjectForm(formData),
    [formData],
  );

  const branchOptions = useMemo(
    () =>
      branches.filter(
        (branch) =>
          formData.company_id &&
          Number(branch.parent_organization) === Number(formData.company_id),
      ),
    [branches, formData.company_id],
  );

  const filteredUsers = useMemo(() => {
    if (!selectedScope.branch_fk) return [];
    return users.filter(
      (user) =>
        isMaster(user) ||
        userMatchesSelectionByIds(getUserOrgIds(user), selectedScope),
    );
  }, [selectedScope, users]);

  const filteredDevices = useMemo(
    () => devices.filter((device) => deviceMatchesScope(device, selectedScope)),
    [devices, selectedScope],
  );
  const selectedBranch = branchOptions.find(branch => String(getBranchId(branch)) === String(formData.branch_id));
  const calculationScope = useMemo(() => ({
    companyId: formData.company_id ? Number(formData.company_id) : null,
    branchId: formData.branch_id ? Number(formData.branch_id) : null,
    branchName: selectedBranch ? getBranchLabel(selectedBranch) : '',
  }), [formData.branch_id, formData.company_id, selectedBranch]);
  const calculationScopeError = useMemo(() => {
    if (!calculationDefinition) return '';
    const sourceIds = (calculationDefinition.nodes || [])
      .filter(node => ['source.basic_parameter', 'source.legacy_calculated_parameter'].includes(node.type))
      .map(node => Number(node.config_json?.device_id));
    const invalid = sourceIds.some(id => {
      const device = devices.find(item => Number(getDeviceId(item)) === id);
      return !device || Number(device.company_fk) !== calculationScope.companyId
        || (calculationScope.branchId != null && Number(device.branch_fk) !== calculationScope.branchId);
    });
    return invalid ? 'El cálculo contiene una medición de otra empresa o sucursal. Editalo antes de crear el proyecto.' : '';
  }, [calculationDefinition, calculationScope, devices]);

  const deviceTypeOptions = useMemo(
    () =>
      toSelectOptions(
        DEVICE_TYPES,
        filteredDevices.map(getDeviceType).concat(
          formData.equipment.map((item) => item.device_type),
        ),
      ),
    [filteredDevices, formData.equipment],
  );

  const equipmentFunctionOptions = useMemo(
    () =>
      toSelectOptions(
        EQUIPMENT_FUNCTIONS,
        projects.flatMap((project) =>
          Array.isArray(project.equipment)
            ? project.equipment.map((item) => item.function_in_project)
            : [],
        ),
      ),
    [projects],
  );

  const filteredProjects = useMemo(() => {
    const text = filters.text.trim().toLowerCase();
    return projects.filter((project) => {
      const matchesStatus = !filters.status || project.status === filters.status;
      const matchesText =
        !text || String(project.title || "").toLowerCase().includes(text);
      return matchesStatus && matchesText;
    });
  }, [filters, projects]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "company_id") {
        next.branch_id = "";
        next.participants = [];
        next.equipment = [];
      }
      if (name === "branch_id") {
        next.participants = [];
        next.equipment = [];
      }
      return next;
    });
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleParticipantChange = (index, field, value) => {
    setFormData((prev) => {
      const participants = prev.participants.slice();
      const nextParticipant = { ...participants[index], [field]: value };
      if (field === "user_id") {
        const selected = filteredUsers.find(
          (user) => String(user.id_user || user.id) === String(value),
        );
        nextParticipant.role = selected?.privilege || "otro";
      }
      participants[index] = nextParticipant;
      return { ...prev, participants };
    });
  };

  const handleEquipmentChange = (index, field, value) => {
    setFormData((prev) => {
      const equipment = prev.equipment.slice();
      const nextItem = { ...equipment[index], [field]: value };
      if (field === "device_id") {
        const selected = filteredDevices.find(
          (device) => String(getDeviceId(device)) === String(value),
        );
        if (selected) {
          nextItem.device_name = selected.name || "";
          nextItem.device_type = getDeviceType(selected) || nextItem.device_type || "";
        }
      }
      equipment[index] = nextItem;
      return { ...prev, equipment };
    });
  };

  const handleTaskChange = (index, field, value) => {
    setFormData((prev) => {
      const tasks = prev.tasks.slice();
      tasks[index] = { ...tasks[index], [field]: value };
      return { ...prev, tasks };
    });
  };

  const buildProjectPayload = () => ({
    title: formData.title.trim(),
    description: formData.description || null,
    status: formData.status,
    progress_percentage: formatProgress(formData.progress_percentage),
    company_id: formData.company_id ? Number(formData.company_id) : null,
    branch_id: formData.branch_id ? Number(formData.branch_id) : null,
    start_date: formData.start_date || null,
    planned_end_date: formData.planned_end_date || null,
    actual_end_date: formData.actual_end_date || null,
    planned_investment_total: toNullableNumber(formData.planned_investment_total),
    actual_investment_total: toNullableNumber(formData.actual_investment_total),
    planned_saving: toNullableNumber(formData.planned_saving),
    actual_saving: toNullableNumber(formData.actual_saving),
    participants: formData.participants
      .filter((participant) => participant.user_id)
      .map((participant) => ({
        user_id: Number(participant.user_id),
        role: participant.role || "otro",
        responsibility: participant.responsibility || null,
        notes: participant.notes || null,
      })),
    equipment: formData.equipment
      .filter(
        (item) =>
          item.device_id ||
          item.device_name ||
          item.device_type ||
          item.description ||
          item.function_in_project,
      )
      .map((item) => ({
        device_id: item.device_id ? Number(item.device_id) : null,
        device_name: item.device_name || null,
        device_type: item.device_type || null,
        description: item.description || null,
        function_in_project: item.function_in_project || null,
      })),
    tasks: formData.tasks
      .filter((task) => task.title?.trim())
      .map((task) => ({
        title: task.title.trim(),
        description: task.description || null,
        status: task.status || "pendiente",
        task_type: task.task_type || "actividad",
        assigned_to: task.assigned_to ? Number(task.assigned_to) : null,
        start_date: task.start_date || null,
        due_date: task.due_date || null,
        planned_hours: toNullableNumber(task.planned_hours),
        planned_cost: toNullableNumber(task.planned_cost),
        progress_percentage: formatProgress(task.progress_percentage),
      })),
  });

  const handleCreateProject = async (event) => {
    event.preventDefault();
    if (!token || !formData.title.trim()) return;
    if (configureCalculation && !calculationDefinition) {
      Toast.fire({ icon: 'warning', title: 'Completá el cálculo del impacto antes de guardar el proyecto' });
      return;
    }

    if (conversionSource) {
      const confirmation = await Swal.fire({
        title: "¿Crear este proyecto de mejora?",
        text: "Se crearán el proyecto y sus tareas, y la recomendación quedará vinculada.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Crear proyecto",
        cancelButtonText: "Seguir editando",
      });
      if (!confirmation.isConfirmed) return;
    }

    setSubmitting(true);
    try {
      const payload = buildProjectPayload();
      const conversionResult = conversionSource
        ? await convertAgentRecommendationToProject(
            conversionSource.recommendationId,
            payload,
            token,
          )
        : null;
      const created = conversionResult?.project || await createProject(payload, token);
      let calculationError = '';
      if (created?.id && configureCalculation && calculationDefinition) {
        try {
          const createdModel = await calculationApi.createModel({
            name: `Cálculo - ${created.title || formData.title}`,
            description: `Modelo de cálculo del proyecto ${created.title || formData.title}`,
            code: projectModelCode(created.id),
            company_id: Number(created.company_id || formData.company_id),
            default_timezone: 'America/Argentina/Buenos_Aires',
          });
          const modelId = Number(createdModel.model?.id);
          const draftId = Number(versionIdOf(createdModel.draft));
          await calculationApi.saveDraft(modelId, draftId, calculationDefinition);
          const validation = await calculationApi.validate(modelId, draftId);
          if (!validation.valid) throw new Error(validation.errors?.[0]?.message || 'El cálculo todavía está incompleto.');
          const published = await calculationApi.publish(modelId, draftId);
          await calculationApi.bindProject({ project_id: Number(created.id), model_id: modelId, version_id: Number(versionIdOf(published)), purpose: 'primary', is_active: true });
        } catch (calcError) {
          calculationError = calcError.message || 'No se pudo completar el cálculo del impacto.';
        }
      }
      if (calculationError) {
        await Swal.fire({
          icon: 'warning', title: 'Proyecto creado; cálculo pendiente',
          text: `${calculationError} El proyecto no se eliminó. Podés continuar la configuración desde Seguimiento.`,
          confirmButtonText: 'Ir al proyecto',
        });
      } else Toast.fire({
        icon: "success",
        title: conversionResult?.already_converted
          ? "La recomendación ya tenía un proyecto"
          : configureCalculation && calculationDefinition ? "Proyecto y cálculo creados" : "Proyecto creado",
      });
      setShowCreateForm(false);
      setFormData(emptyProjectForm);
      setConversionSource(null);
      setConfigureCalculation(false);
      setCalculationDefinition(null);
      await loadProjects();
      if (created?.id) {
        navigate(`/projects/${created.id}`);
      }
    } catch (error) {
      Toast.fire({
        icon: "error",
        title: "No se pudo crear el proyecto",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="cont-path">
        <p className="path path-cursor" onClick={() => navigate("/")}>
          Escritorio {">"}
        </p>
        <p className="path">Proyectos</p>
      </div>

      <div className="projects-page">
        <header className="agent-page-header">
          <div className="agent-page-header-title">
            <FolderKanban size={28} />
            <div>
              <h1>Proyectos de mejora</h1>
              <p>Seguimiento de proyectos e inversiones planificadas</p>
            </div>
          </div>
          {(canManage || conversionSource) && (
            <button
              type="button"
              className="agent-page-header-action"
              onClick={() => {
                if (showCreateForm) {
                  setShowCreateForm(false);
                  setFormData(emptyProjectForm);
                  setConversionSource(null);
                } else {
                  setShowCreateForm(true);
                }
              }}
            >
              {showCreateForm ? <X size={16} /> : <Plus size={16} />}
              {showCreateForm ? "Cerrar" : "Nuevo proyecto"}
            </button>
          )}
        </header>

        {showCreateForm && (canManage || conversionSource) && (
          <section className="projects-card projects-create-form">
            <div className="projects-card-header">
              <h2>{conversionSource ? "Crear proyecto desde recomendación" : "Crear proyecto"}</h2>
              <p>{loadingOptions ? "Cargando opciones..." : conversionSource ? "Revisá y editá los datos antes de confirmar la conversión" : "Datos generales, participantes y equipos"}</p>
            </div>
            <form onSubmit={handleCreateProject}>
              <div className="projects-form-grid">
                <label className="projects-field projects-field-full">
                  <span>Titulo *</span>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    required
                  />
                </label>
                <label className="projects-field projects-field-full">
                  <span>Descripcion *</span>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows={3}
                    required
                  />
                </label>
                <label className="projects-field">
                  <span>Estado *</span>
                  <select name="status" value={formData.status} onChange={handleFormChange}>
                    {PROJECT_STATUSES.filter((status) => status !== "pausado").map((status) => (
                      <option key={status} value={status}>
                        {formatStatusLabel(status)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="projects-field">
                  <span>Avance % *</span>
                  <input
                    type="number"
                    name="progress_percentage"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.progress_percentage}
                    onChange={handleFormChange}
                  />
                </label>
                <label className="projects-field">
                  <span>Compania *</span>
                  <select name="company_id" value={formData.company_id} onChange={handleFormChange} required>
                    <option value="">Seleccionar</option>
                    {companies.map((company) => {
                      const companyId = getCompanyId(company);
                      return (
                        <option key={companyId} value={companyId}>
                          {getCompanyLabel(company)}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="projects-field">
                  <span>Sucursal *</span>
                  <select
                    name="branch_id"
                    value={formData.branch_id}
                    onChange={handleFormChange}
                    disabled={!formData.company_id}
                    required
                  >
                    <option value="">Seleccionar</option>
                    {branchOptions.map((branch) => {
                      const branchId = getBranchId(branch);
                      return (
                        <option key={branchId} value={branchId}>
                          {getBranchLabel(branch)}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="projects-field">
                  <span>Fecha inicio *</span>
                  <input type="date" name="start_date" value={formData.start_date} onChange={handleFormChange} />
                </label>
                <label className="projects-field">
                  <span>Fin planificado *</span>
                  <input type="date" name="planned_end_date" value={formData.planned_end_date} onChange={handleFormChange} />
                </label>
                <label className="projects-field">
                  <span>Fin real</span>
                  <input type="date" name="actual_end_date" value={formData.actual_end_date} onChange={handleFormChange} />
                </label>
                <label className="projects-field">
                  <span>Inversion planificada</span>
                  <input type="number" name="planned_investment_total" min="0" step="0.01" value={formData.planned_investment_total} onChange={handleFormChange} />
                </label>
                <label className="projects-field">
                  <span>Inversion real</span>
                  <input type="number" name="actual_investment_total" min="0" step="0.01" value={formData.actual_investment_total} onChange={handleFormChange} />
                </label>
                <label className="projects-field">
                  <span>Ahorro planificado</span>
                  <input type="number" name="planned_saving" min="0" step="0.01" value={formData.planned_saving} onChange={handleFormChange} />
                </label>
                <label className="projects-field">
                  <span>Ahorro real</span>
                  <input type="number" name="actual_saving" min="0" step="0.01" value={formData.actual_saving} onChange={handleFormChange} />
                </label>
              </div>

              <div className="projects-form-section">
                <div className="projects-section-title">
                  <h3>Participantes</h3>
                  <button
                    type="button"
                    className="projects-btn projects-btn-secondary"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        participants: [...prev.participants, { ...emptyParticipant }],
                      }))
                    }
                  >
                    <Plus size={14} />
                    Agregar
                  </button>
                </div>
                {formData.participants.length === 0 ? (
                  <p className="projects-inline-empty">Sin participantes cargados.</p>
                ) : (
                  <div className="projects-edit-list">
                    {formData.participants.map((participant, index) => (
                      <div className="projects-edit-row" key={`participant-${index}`}>
                        <label className="projects-field">
                          <span>Usuario</span>
                          <select
                            value={participant.user_id}
                            onChange={(event) => handleParticipantChange(index, "user_id", event.target.value)}
                          >
                            <option value="">Seleccionar</option>
                            {filteredUsers.map((user) => (
                              <option key={user.id_user || user.id} value={user.id_user || user.id}>
                                {getUserLabel(user)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Rol</span>
                          <input value={formatStatusLabel(participant.role)} disabled readOnly />
                        </label>
                        <label className="projects-field">
                          <span>Responsabilidad</span>
                          <select value={participant.responsibility} onChange={(event) => handleParticipantChange(index, "responsibility", event.target.value)}>
                            <option value="">Seleccionar</option>
                            {PARTICIPANT_RESPONSIBILITIES.map((responsibility) => (
                              <option key={responsibility} value={responsibility}>
                                {responsibility}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Notas</span>
                          <input value={participant.notes} onChange={(event) => handleParticipantChange(index, "notes", event.target.value)} />
                        </label>
                        <button
                          type="button"
                          className="projects-btn projects-btn-danger"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              participants: prev.participants.filter((_, idx) => idx !== index),
                            }))
                          }
                        >
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="projects-form-section">
                <div className="projects-section-title">
                  <h3>Equipos</h3>
                  <button
                    type="button"
                    className="projects-btn projects-btn-secondary"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        equipment: [...prev.equipment, { ...emptyEquipment }],
                      }))
                    }
                  >
                    <Plus size={14} />
                    Agregar
                  </button>
                </div>
                {formData.equipment.length === 0 ? (
                  <p className="projects-inline-empty">Sin equipos asociados.</p>
                ) : (
                  <div className="projects-edit-list">
                    {formData.equipment.map((item, index) => (
                      <div className="projects-edit-row projects-edit-row-equipment" key={`equipment-${index}`}>
                        <label className="projects-field">
                          <span>Equipo existente</span>
                          <select value={item.device_id} onChange={(event) => handleEquipmentChange(index, "device_id", event.target.value)}>
                            <option value="">Sin vincular</option>
                            {filteredDevices.map((device) => {
                              const deviceId = getDeviceId(device);
                              return (
                                <option key={deviceId} value={deviceId}>
                                  {getDeviceLabel(device)}
                                </option>
                              );
                            })}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Nombre</span>
                          <input value={item.device_name} onChange={(event) => handleEquipmentChange(index, "device_name", event.target.value)} />
                        </label>
                        <label className="projects-field">
                          <span>Tipo</span>
                          <select value={item.device_type} onChange={(event) => handleEquipmentChange(index, "device_type", event.target.value)}>
                            <option value="">Seleccionar</option>
                            {deviceTypeOptions.map((type) => (
                              <option key={type} value={type}>
                                {formatStatusLabel(type)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Funcion</span>
                          <select value={item.function_in_project} onChange={(event) => handleEquipmentChange(index, "function_in_project", event.target.value)}>
                            <option value="">Seleccionar</option>
                            {equipmentFunctionOptions.map((func) => (
                              <option key={func} value={func}>
                                {formatStatusLabel(func)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field projects-field-full">
                          <span>Descripcion</span>
                          <input value={item.description} onChange={(event) => handleEquipmentChange(index, "description", event.target.value)} />
                        </label>
                        <button
                          type="button"
                          className="projects-btn projects-btn-danger"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              equipment: prev.equipment.filter((_, idx) => idx !== index),
                            }))
                          }
                        >
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="projects-form-section">
                <div className="projects-section-title">
                  <h3>Tareas iniciales</h3>
                  <button
                    type="button"
                    className="projects-btn projects-btn-secondary"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        tasks: [...prev.tasks, { ...emptyTask }],
                      }))
                    }
                  >
                    <Plus size={14} />
                    Agregar
                  </button>
                </div>
                {formData.tasks.length === 0 ? (
                  <p className="projects-inline-empty">Sin tareas iniciales.</p>
                ) : (
                  <div className="projects-edit-list">
                    {formData.tasks.map((task, index) => (
                      <div className="projects-edit-row" key={`task-${index}`}>
                        <label className="projects-field">
                          <span>Título *</span>
                          <input value={task.title} onChange={(event) => handleTaskChange(index, "title", event.target.value)} required />
                        </label>
                        <label className="projects-field">
                          <span>Responsable</span>
                          <select value={task.assigned_to} onChange={(event) => handleTaskChange(index, "assigned_to", event.target.value)}>
                            <option value="">Sin asignar</option>
                            {filteredUsers.map((user) => (
                              <option key={user.id_user || user.id} value={user.id_user || user.id}>{getUserLabel(user)}</option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Estado</span>
                          <select value={task.status} onChange={(event) => handleTaskChange(index, "status", event.target.value)}>
                            {TASK_STATUSES.map((status) => <option key={status} value={status}>{formatStatusLabel(status)}</option>)}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Tipo</span>
                          <select value={task.task_type} onChange={(event) => handleTaskChange(index, "task_type", event.target.value)}>
                            {TASK_TYPES.map((type) => <option key={type} value={type}>{formatStatusLabel(type)}</option>)}
                          </select>
                        </label>
                        <label className="projects-field projects-field-full">
                          <span>Descripción</span>
                          <input value={task.description} onChange={(event) => handleTaskChange(index, "description", event.target.value)} />
                        </label>
                        <label className="projects-field">
                          <span>Inicio</span>
                          <input type="date" value={task.start_date} onChange={(event) => handleTaskChange(index, "start_date", event.target.value)} />
                        </label>
                        <label className="projects-field">
                          <span>Fecha objetivo</span>
                          <input type="date" value={task.due_date} onChange={(event) => handleTaskChange(index, "due_date", event.target.value)} />
                        </label>
                        <label className="projects-field">
                          <span>Horas planificadas</span>
                          <input type="number" min="0" step="0.25" value={task.planned_hours} onChange={(event) => handleTaskChange(index, "planned_hours", event.target.value)} />
                        </label>
                        <label className="projects-field">
                          <span>Costo planificado</span>
                          <input type="number" min="0" step="0.01" value={task.planned_cost} onChange={(event) => handleTaskChange(index, "planned_cost", event.target.value)} />
                        </label>
                        <button
                          type="button"
                          className="projects-btn projects-btn-danger"
                          onClick={() => setFormData((prev) => ({
                            ...prev,
                            tasks: prev.tasks.filter((_, taskIndex) => taskIndex !== index),
                          }))}
                        >
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="projects-form-section">
                <div className="projects-section-title">
                  <div><h3>Cálculo del impacto</h3><p>Podés definir ahora cómo SIGER medirá el resultado del proyecto.</p></div>
                  <div className="guided-impact-actions">
                    <button type="button" className={`projects-btn ${configureCalculation ? 'projects-btn-primary' : 'projects-btn-secondary'}`} disabled={!formData.company_id || !formData.branch_id} onClick={() => setConfigureCalculation(true)}>Configurar ahora</button>
                    <button type="button" className="projects-btn projects-btn-secondary" onClick={() => setConfigureCalculation(false)}>Configurar después</button>
                  </div>
                </div>
                {!formData.company_id || !formData.branch_id ? <p className="projects-inline-empty">Seleccioná primero la empresa y la sucursal del proyecto.</p> : configureCalculation ? <>
                  <GuidedImpactCalculation
                    graph={{ id: null, model_id: null, status: 'draft', nodes: calculationDefinition?.nodes || [], edges: calculationDefinition?.edges || [], default_timezone: 'America/Argentina/Buenos_Aires', default_frequency: 'day' }}
                    api={calculationApi}
                    editable
                    organizationScope={calculationScope}
                    allowAdvanced={false}
                    deferredSave
                    onDefinitionChange={setCalculationDefinition}
                  />
                  {!calculationDefinition && <p className="projects-inline-empty">Completá la medición y el nombre del resultado. El cálculo se guardará junto con el proyecto.</p>}
                  {calculationScopeError && <p className="project-calculation-error">{calculationScopeError}</p>}
                </> : <p className="projects-inline-empty">Podrás configurarlo luego desde Seguimiento.</p>}
              </div>

              <div className="projects-form-actions">
                <button
                  type="button"
                  className="projects-btn projects-btn-secondary"
                  onClick={() => {
                    setShowCreateForm(false);
                    setFormData(emptyProjectForm);
                    setConversionSource(null);
                    setConfigureCalculation(false);
                    setCalculationDefinition(null);
                  }}
                >
                  Cancelar
                </button>
                <button type="submit" className="projects-btn projects-btn-primary" disabled={submitting || Boolean(calculationScopeError) || (configureCalculation && !calculationDefinition)}>
                  {submitting
                    ? "Guardando..."
                    : conversionSource
                      ? "Crear y vincular proyecto"
                      : configureCalculation ? "Guardar proyecto y cálculo" : "Guardar proyecto"}
                </button>
              </div>
            </form>
          </section>
        )}

        <section className="projects-card">
          <div className="projects-card-header projects-list-header">
            <div>
              <h2>Listado de proyectos</h2>
              <p>{filteredProjects.length} de {projects.length} proyecto(s)</p>
            </div>
            <div className="projects-filters">
              <label className="projects-search">
                <Search size={16} />
                <input
                  type="search"
                  name="text"
                  placeholder="Buscar por titulo"
                  value={filters.text}
                  onChange={handleFilterChange}
                />
              </label>
              <select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">Todos los estados</option>
                {PROJECT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {formatStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <p className="projects-empty">Cargando proyectos...</p>
          ) : filteredProjects.length === 0 ? (
            <p className="projects-empty">No hay proyectos para los filtros seleccionados.</p>
          ) : (
            <div className="projects-table-wrapper">
              <table className="projects-table">
                <thead>
                  <tr>
                    <th>Titulo</th>
                    <th>Estado</th>
                    <th>Avance</th>
                    <th>Monto planificado</th>
                    <th>Monto invertido</th>
                    <th>Inicio</th>
                    <th>Fin planificado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => {
                    const progress = formatProgress(project.progress_percentage);
                    return (
                      <tr key={project.id}>
                        <td>
                          <div className="projects-project-name">{project.title}</div>
                        </td>
                        <td>
                          <span className={`projects-status-badge projects-status-${project.status}`}>
                            {formatStatusLabel(project.status)}
                          </span>
                        </td>
                        <td>
                          <div className="projects-progress-container">
                            <progress className="projects-progress-native" max="100" value={progress} />
                            <span className="projects-progress-label">{progress}%</span>
                          </div>
                        </td>
                        <td>{formatCurrency(project.planned_investment_total)}</td>
                        <td>{formatCurrency(project.actual_investment_total)}</td>
                        <td>{formatDate(project.start_date)}</td>
                        <td>{formatDate(project.planned_end_date)}</td>
                        <td>
                          <button
                            type="button"
                            className="projects-btn projects-btn-view"
                            onClick={() => navigate(`/projects/${project.id}`)}
                          >
                            Ver detalle
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default Projects;
